/**
 * usePageContextReporter — mounted ONCE in MainLayout (right after useActivityTracker).
 * Tells the Control Plane what page the user is on so Zaviago AI understands
 * "this product" / "these orders" (plan 1968 F4). Invisible: no UI, no toasts.
 *
 * Runs only for stores with the zaviago_ai app installed, whether or not the AI panel
 * is open (the context must already be there when the user asks). Triggers:
 * navigation, detail registry changes (record loaded, title, tab), list publish,
 * page.extra, store / language changes — all debounced into one send. Re-posts when
 * the AI panel opens, when the tab becomes visible again, and every ~4 min while visible.
 *
 * Screen snapshot (plan 1968 F13): only while the AI panel is open. Captured at send
 * time from the published screen sections (or the page text fallback); section changes
 * re-post through the same debounce, and a light re-check every few seconds picks up
 * content that loaded later — an unchanged screen is never re-sent (reporter dedupe).
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppAccess } from "@/hooks/useAppAccess";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useStoreList } from "@/hooks/useStoreList";
import { useLanguage } from "@/i18n/useLanguage";
import { useAuthStore } from "@/lib/store/authStore";
import { useOptionalActiveDetailPageContext } from "@/contexts/ActiveDetailPageContext";
import { ZAVIAGO_AI_APP_KEY } from "@/components/layout/rightRailConstants";
import { buildPageSnapshot } from "./buildPageSnapshot";
import { getPageLabel } from "./pageLabel";
import { pageContextStore, selectExtraForPath } from "./pageContextStore";
import { postPageContext } from "./postPageContext";
import { captureScreenText } from "./screenDom";
import { buildScreenContext } from "./screenSections";
import {
  createPageContextReporter,
  PAGE_CONTEXT_HEARTBEAT_MS,
  type PageContextReporter,
} from "./reporter";
import { resolvePageRoute } from "./resolvePageRoute";

const DESKTOP_QUERY = "(min-width: 768px)";
/** While the AI panel is open: re-check the screen this often (sent only if it changed). */
export const SCREEN_RECHECK_MS = 5000;

function captureScreen(path: string, at: string) {
  const { aiPanelOpen, screenParts } = pageContextStore.getState();
  return buildScreenContext({ aiPanelOpen, parts: screenParts, pathname: path, at, readText: () => captureScreenText() });
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

export function usePageContextReporter(): void {
  const location = useLocation();
  const { appAccessLoaded, isInstalled } = useAppAccess();
  const enabled = appAccessLoaded && isInstalled(ZAVIAGO_AI_APP_KEY);

  const currentTenant = useAuthStore((s) => s.currentTenant);
  const tenantCompany = useAuthStore((s) => s.tenantCompany);
  const storefrontKey = useAuthStore((s) => s.activeStorefrontKey);
  const username = useAuthStore((s) => s.username);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const role = useAuthStore((s) => s.role);
  const appAccess = useAuthStore((s) => s.appAccess);
  const { language } = useLanguage();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const { data: stores } = useStoreList({ enabled });
  const storefrontLabel = storefrontKey
    ? stores?.find((s) => s.storefront_key === storefrontKey)?.label
    : undefined;

  const registry = useOptionalActiveDetailPageContext();
  const list = pageContextStore((s) => s.list);
  const extras = pageContextStore((s) => s.extras);
  const aiPanelOpenedAt = pageContextStore((s) => s.aiPanelOpenedAt);
  const aiPanelOpen = pageContextStore((s) => s.aiPanelOpen);
  const screenParts = pageContextStore((s) => s.screenParts);

  const reporterRef = useRef<PageContextReporter | null>(null);
  if (!reporterRef.current) {
    reporterRef.current = createPageContextReporter({ send: postPageContext, getScreen: captureScreen });
  }

  // Latest snapshot builder in a ref, so registry notifications (no re-render) use fresh values
  const buildRef = useRef<() => void>(() => undefined);
  buildRef.current = () => {
    if (!enabled) return;
    const route = resolvePageRoute(location.pathname);
    reporterRef.current?.update(
      buildPageSnapshot({
        pathname: location.pathname,
        search: location.search,
        route,
        label: getPageLabel(location.pathname),
        activeRecord: registry?.getActive() ?? null,
        list,
        extra: selectExtraForPath(extras, location.pathname),
        ui: { language, isDesktop },
        scope: {
          tenant: currentTenant,
          company: tenantCompany,
          storefrontKey,
          ...(storefrontLabel ? { storefrontLabel } : {}),
        },
        user: { email: username, isAdmin, role, appAccess },
        timezone: browserTimezone(),
      }),
    );
  };

  // Any input changed -> (debounced) report
  useEffect(() => {
    buildRef.current();
  }, [
    enabled,
    location.pathname,
    location.search,
    registry?.activeVersion,
    list,
    extras,
    aiPanelOpen,
    screenParts,
    language,
    isDesktop,
    currentTenant,
    tenantCompany,
    storefrontKey,
    storefrontLabel,
    username,
    isAdmin,
    role,
    appAccess,
  ]);

  // Registry notifications (title loaded, tab switched) without re-rendering MainLayout
  const subscribe = registry?.subscribe;
  useEffect(() => {
    if (!subscribe) return;
    return subscribe(() => buildRef.current());
  }, [subscribe]);

  // New tenant / user / AI turned off -> forget history and anything pending
  useEffect(() => {
    if (!enabled) reporterRef.current?.reset();
  }, [enabled]);
  useEffect(() => {
    return () => reporterRef.current?.reset();
  }, [currentTenant, username]);

  // AI panel opened -> send the current page now
  useEffect(() => {
    if (enabled && aiPanelOpenedAt > 0) reporterRef.current?.repost();
  }, [aiPanelOpenedAt, enabled]);

  // AI panel open: re-check the screen (content that loaded after the last post)
  useEffect(() => {
    if (!enabled || !aiPanelOpen) return;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") buildRef.current();
    }, SCREEN_RECHECK_MS);
    return () => clearInterval(interval);
  }, [enabled, aiPanelOpen]);

  // Heartbeat while visible + re-post when the tab becomes visible again
  useEffect(() => {
    if (!enabled) return;
    const isVisible = () => document.visibilityState === "visible";
    const interval = setInterval(() => {
      if (isVisible()) reporterRef.current?.repost();
    }, PAGE_CONTEXT_HEARTBEAT_MS);
    const onVisibility = () => {
      if (isVisible()) reporterRef.current?.repost();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled]);

  useEffect(() => {
    const reporter = reporterRef.current;
    return () => reporter?.dispose();
  }, []);
}
